/**
 * What happens to the primary write when the ledger write fails.
 *
 * Two opposite policies existed in the product before this file, and both were defensible:
 *
 * - **The asset register refuses the row.** `save-entity` writes the events first and returns an
 *   error without touching the asset, because a balance is a projection over the ledger and a row
 *   saved without its event is a number with no provenance.
 * - **The chemical store ledger never throws.** The movement stands and the failure is logged back
 *   to the caller, because stock quantities live in the product's inventory cache and a failed
 *   ledger write must not cost a farmer their stock figures.
 *
 * They differ because they disagree about what the ledger is *for*, so the module states that once
 * and lets the consumer declare which case it is in. **Decided 16 September 2026 (Craig):**
 * fail-closed where the ledger is the record, fail-open where it is a derived index. See
 * `devdocs/shared/ledger-and-reporting/ledger-and-reports-scope.md` §2.7.
 *
 * Nothing here performs a write or reads a clock. It is a decision, and the caller owns the I/O -
 * the same split as `fmd-entities/asset-triggers/trigger-contract.js`.
 */

/**
 * What the ledger is for, on this type. The declared fact; the policy is derived from it.
 *
 * `record` - the event series is the truth and current state is replayed from it.
 * `index` - the truth is somewhere else (a row, a cache) and the ledger explains how it got there.
 */
import i18next from 'i18next'

export const LEDGER_ROLES = { RECORD: 'record', INDEX: 'index' }

/**
 * `fail-closed` - the primary write is refused. Nothing is saved, and the caller reports it.
 * `fail-open` - the primary write stands. The failure is returned to the caller, never swallowed.
 */
export const WRITE_FAILURE_POLICIES = { FAIL_CLOSED: 'fail-closed', FAIL_OPEN: 'fail-open' }

export const isLedgerRole = role => role === LEDGER_ROLES.RECORD || role === LEDGER_ROLES.INDEX

/**
 * Derive the policy from the role.
 *
 * Deliberately total on the two roles and deliberately **not** defaulted: an unknown role throws
 * rather than quietly picking one. A silent default is the convention each consumer rediscovers,
 * which is the thing this field exists to remove.
 */
export const policyForLedgerRole = role => {
    if (role === LEDGER_ROLES.RECORD) return WRITE_FAILURE_POLICIES.FAIL_CLOSED
    if (role === LEDGER_ROLES.INDEX) return WRITE_FAILURE_POLICIES.FAIL_OPEN
    throw new Error(`policyForLedgerRole: unknown ledgerRole ${JSON.stringify(role)} - declare 'record' or 'index'`)
}

/**
 * Decide what a caller does now that its ledger write has failed.
 *
 * `written` is how far a multi-event plan got before it stopped - a move is a departure and an
 * arrival, and a caller that proceeds needs to know it is holding half a pair. It is passed through
 * rather than interpreted, because only the consumer knows whether its own partial is recoverable.
 *
 * @param {{ policy: string, error?: string, written?: Array }} outcome
 * @returns {{ proceed: boolean, policy: string, error: string, written: Array, partial: boolean }}
 *   `proceed` is whether the primary write goes ahead. `partial` says some events landed and the
 *   rest did not, which is the state a fail-open consumer has to surface rather than log.
 */
export const decideOnLedgerFailure = ({ policy, error, written = [] } = {}) => {
    if (policy !== WRITE_FAILURE_POLICIES.FAIL_CLOSED && policy !== WRITE_FAILURE_POLICIES.FAIL_OPEN) {
        throw new Error(`decideOnLedgerFailure: unknown policy ${JSON.stringify(policy)}`)
    }
    return {
        proceed: policy === WRITE_FAILURE_POLICIES.FAIL_OPEN,
        policy,
        error: error || i18next.t('8440'),
        written,
        partial: written.length > 0,
    }
}

export default { LEDGER_ROLES, WRITE_FAILURE_POLICIES, isLedgerRole, policyForLedgerRole, decideOnLedgerFailure }
